import {
  getReviewerListApi,
  getReviewProcessApi,
  getReviewProjectApi,
  getReviewTaskSummaryListApi
} from '@/api/hall/review-hall'
import { getBiddersMarksApi } from '@/api/hall/bid-open-hall'
import { getReviewProjectInSupplierApi } from '@/api/hall/review-hall-supplier'
import {
  reviewProcessStatusDict,
  reviewProcessTypeDict
} from '@/dictionary/hall/review-hall'
import { Message } from 'element-ui'
import {
  bidFileTypeDict,
  kpbProjectStatusDict,
  reviewModelDict
} from '@/dictionary/project/kpb-project'
import { getQuoteOpenMarkPageApi } from '@/api/hall/quote-open'
import { getMoneyInCn } from '@/utils/utils'

const state = {
  /**
   * 评审项目id
   */
  projectId: 0,

  /**
   * 评审大厅进度
   */
  processList: [],

  /**
   * 当前的评审项目
   */
  project: {},

  /**
   * 评审办法列表
   */
  reviewTaskSummaryList: [],

  /**
   * 评审专家列表
   */
  reviewerList: [],

  /**
   * 唱标状态
   */
  chantPlay: false,

  /**
   * 唱标内容
   */
  chantPlayContent: '',

  /**
   * 显示播放器
   */
  isAudioShow: false,

  /**
   * 开标大厅在线供应商
   */
  KpbBiddersOnlineInfo: {},

  /**
   * 开标大厅在线供应商
   */
  isShowKpbBiddersOnlineNum: false
}

const getters = {
  // 是否是双信封
  isDouble: state => {
    return (
      state.project?.projectInfo?.reviewModel === reviewModelDict.DOUBLE.value
    )
  },

  // 能生成一阶段评审报告
  canCreateFirstStageReport: (state, getter) => {
    // 仅双信封可以生成一阶段评审
    if (getter.isDouble) {
      // offerBidOpenStatus 大于等于1，表示一阶段结束，等于5表示二阶段开始
      // 一阶段结束后直到评审结束前，都可以生成一阶段评审报告
      return (
        state.project?.offerBidOpenStatus &&
        state.project?.projectStatus !==
          kpbProjectStatusDict.EVALUATION_ENDED.value
      )
    }

    return false
  },

  processTab: (state, getter) => {
    return state.processList.map(process => {
      const processTypeObj = reviewProcessTypeDict.translate(
        process.processType
      )
      if (processTypeObj.value === reviewProcessTypeDict.REPORT.value) {
        // 针对评标报告，双信封中，要求一阶段评审结束后，可生成一阶段评审报告
        if (getter.canCreateFirstStageReport) {
          return {
            label: processTypeObj.label,
            value: process.processType,
            routeName: reviewProcessTypeDict.REPORT_FIRST.routeName,
            active: true
          }
        }
      }
      return {
        label: processTypeObj.label,
        value: process.processType,
        routeName: processTypeObj.routeName,
        active:
          process.processStatus !== reviewProcessStatusDict.NOT_STARTED.value
      }
    })
  },

  playContent: state => {
    return state.chantPlayContent
  }
}

const mutationsMap = {
  SET_PROJECT_ID: 'SET_PROJECT_ID',
  SET_PROCESS_LIST: 'SET_PROCESS_LIST',
  SET_PROJECT: 'SET_PROJECT',
  SET_REVIEW_TASK_SUMMARY_LIST: 'SET_REVIEW_TASK_SUMMARY_LIST',
  SET_REVIEWER_LIST: 'SET_REVIEWER_LIST',
  SET_KPB_BIDDERS_ONLINE_LIST: 'SET_KPB_BIDDERS_ONLINE_LIST',
  SET_IS_SHOW_KPB_BIDDERS_ONLINE_NUM: 'SET_IS_SHOW_KPB_BIDDERS_ONLINE_NUM',
  SET_CHANT_PLAY_CONTENT: 'SET_CHANT_PLAY_CONTENT'
}

const mutations = {
  [mutationsMap.SET_PROJECT_ID]: (state, id) => {
    state.projectId = id
  },

  [mutationsMap.SET_PROCESS_LIST]: (state, processList) => {
    state.processList = processList || []
  },

  [mutationsMap.SET_PROJECT]: (state, project) => {
    state.project = project
  },

  [mutationsMap.SET_REVIEW_TASK_SUMMARY_LIST]: (state, list) => {
    const tempList = list || []
    const tempTaskList = []
    state.project.wayList.forEach(way => {
      tempTaskList.push(tempList.find(item => item.reviewWayId === way.wayId))
    })
    state.reviewTaskSummaryList = tempTaskList
  },

  [mutationsMap.SET_REVIEWER_LIST]: (state, reviewerList) => {
    state.reviewerList = reviewerList || []
  },

  SET_CHANT_PLAY: (state, chantPlay) => {
    state.chantPlay = chantPlay
  },

  SET_IS_AUDIO_SHOW: (state, isAudioShow) => {
    state.isAudioShow = isAudioShow
  },

  SET_KPB_BIDDERS_ONLINE_LIST: (state, KpbBiddersOnlineInfo) => {
    state.KpbBiddersOnlineInfo = KpbBiddersOnlineInfo
  },

  SET_IS_SHOW_KPB_BIDDERS_ONLINE_NUM: (state, isShowKpbBiddersOnlineNum) => {
    state.isShowKpbBiddersOnlineNum = isShowKpbBiddersOnlineNum
  },

  SET_CHANT_PLAY_CONTENT: (state, chantPlayContent) => {
    state.chantPlayContent = chantPlayContent
  }
}

const actions = {
  /**
   * 获取评审大厅进度信息
   */
  getReviewProcess({ state, commit }) {
    return getReviewProcessApi(state.projectId)
      .then(res => {
        const { data = [] } = res
        commit(mutationsMap.SET_PROCESS_LIST, data)
        return res
      })
      .catch(err => {
        if (err?.message) {
          Message.error(err.message)
        }
        throw err
      })
  },

  /**
   * 获取项目信息
   */
  getReviewProject({ state, commit }) {
    return getReviewProjectApi(state.projectId)
      .then(res => {
        const { data = {} } = res
        commit(mutationsMap.SET_PROJECT, data)
        return res
      })
      .catch(err => {
        if (err?.message) {
          Message.error(err.message)
        }
        throw err
      })
  },

  /**
   * 获取项目信息（供应商）
   */
  getReviewProjectInSupplier({ state, commit }) {
    return getReviewProjectInSupplierApi(state.projectId)
      .then(res => {
        const { data = {} } = res
        commit(mutationsMap.SET_PROJECT, data)
        return res
      })
      .catch(err => {
        if (err?.message) {
          Message.error(err.message)
        }
        throw err
      })
  },

  /**
   * 获取评审专家列表
   */
  getReviewerList({ state, commit }) {
    return getReviewerListApi(state.projectId)
      .then(res => {
        const { data = [] } = res
        const list = data.sort((a, b) => {
          if (a.isLeader && !b.isLeader) {
            return -1
          }
        })
        commit(mutationsMap.SET_REVIEWER_LIST, list)
        return res
      })
      .catch(err => {
        if (err?.message) {
          Message.error(err.message)
        }
        throw err
      })
  },

  /**
   * 获取评审办法列表
   */
  getReviewTaskSummaryList({ state, commit }, processId) {
    return getReviewTaskSummaryListApi(state.projectId, processId)
      .then(res => {
        const { data = [] } = res
        commit(mutationsMap.SET_REVIEW_TASK_SUMMARY_LIST, data)
        return res
      })
      .catch(err => {
        if (err?.message) {
          Message.error(err.message)
        }
        throw err
      })
  },

  /**
   * 修改唱标播放状态
   */
  changeChantPlay({ commit }, status) {
    commit('SET_CHANT_PLAY', status)
  },

  /**
   * 唱标是否展示
   */
  changeAudioShow({ commit }, status) {
    commit('SET_IS_AUDIO_SHOW', status)
  },

  /**
   * 获取开标大厅在线供应商列表
   */
  getBiddersOnline({ commit }, KpbBiddersOnlineInfo) {
    commit('SET_KPB_BIDDERS_ONLINE_LIST', KpbBiddersOnlineInfo)
  },

  /**
   * 是否显示在线供应商
   */
  showBiddersOnline({ commit }, status) {
    commit('SET_IS_SHOW_KPB_BIDDERS_ONLINE_NUM', status)
  },

  /**
   * 获取唱标内容
   */
  changeChantPlayContent(
    { commit },
    { projectId, bidFileType = bidFileTypeDict.BUSINESS.value }
  ) {
    const handleMarkContent = data => {
      let content = ''
      if (data && data.length) {
        data.map(dataItem => {
          if (dataItem && dataItem?.itemList?.length) {
            dataItem.itemList.map(item => {
              let markValue = ''
              if (item.markUnit == '%') {
                markValue = '百分之' + item.markValue
              } else if (item.markUnit == '万元') {
                // 单位万元 唱标处理
                markValue = getMoneyInCn(Number(item.markValue), 10000)
              } else if (item.markUnit == '元') {
                // 单位元唱标处理
                markValue = getMoneyInCn(item.markValue)
              } else {
                markValue = item.markValue
              }
              content += item.markName + '：' + markValue + '，'
            })
            if (bidFileType === bidFileTypeDict.BUSINESS.value) {
              content += dataItem.openBidRemark + '。'
            } else {
              content += dataItem.openOfferBidRemark + '。'
            }
          }
        })
      }
      return content
    }

    return new Promise((resolve, reject) => {
      if (bidFileType === bidFileTypeDict.QUOTATION.value) {
        getQuoteOpenMarkPageApi(projectId, { isListing: 1 })
          .then(res => {
            const data = res.data?.records || []
            const content = handleMarkContent(data)
            commit('SET_CHANT_PLAY_CONTENT', content)
            resolve(content)
          })
          .catch(err => {
            if (err?.message) {
              Message.error(err.message)
              reject(err)
            }
            throw err
          })
      } else {
        getBiddersMarksApi(projectId)
          .then(res => {
            const { data = {} } = res
            const content = handleMarkContent(data)
            commit('SET_CHANT_PLAY_CONTENT', content)
            resolve(content)
          })
          .catch(err => {
            if (err?.message) {
              Message.error(err.message)
              reject(err)
            }
            throw err
          })
      }
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
